import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_INHERITANCE } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { Inheritance, ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  GeneralInheritanceFormValue,
  GeneralInheritanceRulesSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.types';
import { getGeneralInheritanceRulesSearchableKeywords } from '../../../../utils/uiSettings/general/general.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzGeneralInheritanceRulesTableWebComponent } from '../gridComponents/GzGeneralInheritanceRulesTableWebComponent';

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_INHERITANCE)
export class GzGeneralInheritanceRulesWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  GeneralInheritanceFormValue,
  GeneralInheritanceRulesSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.POLICY_SETTINGS,
    sectionId: SectionId.GENERAL_INHERITANCE,
  };
  protected searchableKeywords = getGeneralInheritanceRulesSearchableKeywords();

  @query('#inheritance-form-field')
  private inheritanceField: GzFormFieldWebComponent<GzGeneralInheritanceRulesTableWebComponent, Inheritance[]>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-title
      class="content"
      searchable-label="${this.searchableKeywords[GeneralInheritanceRulesSearchableKeywords.InheritanceRules].label}"
      ><span
        >${this.searchableKeywords[GeneralInheritanceRulesSearchableKeywords.InheritanceRules].translatedLabel}</span
      >
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
      >${translateFn(
        'Reuse settings from existing policies to build your policy. Select the module, section, and policy from the drop-down menus.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="mt-4">
        <gz-title
          fontWeight="${FontWeights.SEMIBOLD}"
          fontSize="${FontSizes.PARAGRAPH_12}"
          class="subtitle"
          searchable-label="${this.searchableKeywords[GeneralInheritanceRulesSearchableKeywords.AddInheritanceRules]
            .label}"
          >${this.searchableKeywords[GeneralInheritanceRulesSearchableKeywords.AddInheritanceRules]
            .translatedLabel}</gz-title
        >

        <gz-form-field id="inheritance-form-field">
          <gz-policy-panel-general-inheritance-table
            class="removeGridPadding"
            gz-form-input
          ></gz-policy-panel-general-inheritance-table>
        </gz-form-field>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - inheritance
   */
  protected getValueFromManager(): GeneralInheritanceFormValue {
    // get default value
    const defaultValue = {
      inheritance: [],
    };

    // get value from manager
    const value = {
      inheritance: PolicyPanelManager.getInstance().getPolicy('inheritance', []),
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      inheritance: this.inheritanceField,
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      inheritance: this.valueT.inheritance,
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['inheritance'], []) as FormGroupValue;
    }

    return value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_GENERAL_INHERITANCE: GzGeneralInheritanceRulesWebComponent;
  }
}
